import { useState } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { financeAttachmentDownloadUrl } from '@/lib/finance';
import { errorMessage } from '@/lib/errors';

export interface AttachmentListItem {
  id: string;
  name?: string;
  mime?: string;
}

export interface AttachmentListProps {
  items: readonly AttachmentListItem[];
  /** Omit for a read-only list (e.g. a payment's own history — spec §14). */
  onRemove?: (id: string) => void;
  className?: string;
}

/**
 * Receipts already uploaded against one target (spec §8.2/§8.3/§14; used by
 * SettleDialog/BatchSettle for the receipts still being composed, and by
 * task 22's submission/expected detail screens for a target's registered
 * ones). Deliberately driven by props the caller already has, not by a
 * fetch of its own — there is no "list attachments by target" endpoint
 * (only presign/register/download-url exist, api/openapi.yaml), so a caller
 * that HAS full FinanceAttachment objects (e.g. FinanceMovement.attachments)
 * can pass id+mime straight through, and one that only ever collected ids
 * from the upload flow (this dialog, before a movement exists to attach
 * them to) can pass those with just the file name it already had locally.
 *
 * Each row resolves its own download URL on click rather than up front —
 * download-url is a 5-minute presigned link (spec §14), so minting one per
 * mount for a receipt nobody opens would be a wasted round trip and a link
 * that may already be expired by the time it is clicked.
 */
export function AttachmentList({ items, onRemove, className }: AttachmentListProps) {
  if (items.length === 0) return null;
  return (
    <ul className={className ?? 'flex flex-col gap-1'}>
      {items.map((it) => (
        <AttachmentRow key={it.id} item={it} onRemove={onRemove} />
      ))}
    </ul>
  );
}

function AttachmentRow({
  item,
  onRemove,
}: {
  item: AttachmentListItem;
  onRemove?: (id: string) => void;
}) {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function open() {
    setError(null);
    setOpening(true);
    try {
      const url = await financeAttachmentDownloadUrl(item.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setOpening(false);
    }
  }

  return (
    <li className="flex items-center justify-between gap-2 rounded-[var(--radius-control)] border border-[var(--border-hairline)] px-3 py-1.5 text-sm">
      <button
        type="button"
        onClick={() => void open()}
        disabled={opening}
        className="flex min-w-0 items-center gap-1.5 truncate text-primary hover:underline disabled:opacity-55"
      >
        <Download className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{item.name ?? `Receipt ${item.id.slice(0, 8)}`}</span>
      </button>
      <span className="flex items-center gap-2">
        {error && (
          <span role="alert" className="text-xs text-destructive">
            {error}
          </span>
        )}
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(item.id)}
            aria-label={`Remove ${item.name ?? 'receipt'}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </span>
    </li>
  );
}
